import React, { useState, useContext, useEffect, useRef } from 'react';
import { SearchBar } from '@nike/nike-design-system-components';
import { node, bool } from 'prop-types';
import FocusTrap from 'focus-trap-react';
import clsx from 'clsx';
import { getLocationSearchParams } from 'nav-utilities';
import { AppContext } from '../../context/appContext';
import { AnalyticsContext } from '../../context/analyticsContext';
import useBodega from './../../utils/hooks/bodegaHook';
import {
  StyledSearch,
  StyledVisualSearchResultsContainer,
  SEARCH_ICON,
  SEARCH_END_ICON,
} from './styles';
import VisualSearchResults from '../VisualSearchResults';
import { BODEGA_DEFAULTS, NIKE_AI } from '../../constants';
import useSearchSuggestions from '../../utils/hooks/searchSuggestionsHook';
import useSearchTermInvalidCallback from '../../utils/hooks/searchTermInvalidHook';
import useSearchDocumentKeyListener from '../../utils/hooks/searchKeyHook';
import { getSearchUrl } from '../../utils/url';
import useLocale from '../../utils/hooks/localeHook';
import useLocalStorage from '../../utils/hooks/localStorageHook';
import { navMenuOpen } from '../../utils/sharedStyles';
import { useNavOptionsContext } from '../../context/optionsContext';
import NikeAISearchIcon from '../NikeAISearchIcon';
import useFeatureFlags from '../../utils/hooks/featureFlagsHook';

const searchInputId = 'gn-search-input';

/**
 * Component that exposes the search bar.
 * @param {Object} props
 * @param {node} props.children components to be rendered with.
 * @param {bool} props.shouldTrapFocus flag indicating whether the focus trap should be active
 * and keeps all focus and actions within the nested components.
 * @returns Visual search component that can be used in both mobile and desktop settings.
 */
const VisualSearch = ({ children, shouldTrapFocus = true }) => {
  const {
    SEARCH,
    SEARCH_PRODUCTS,
    RESET_SEARCH,
    CANCEL,
    SEARCH_EXTENDED,
    PLEASE_ENTER_SEARCH_QUERY,
  } = BODEGA_DEFAULTS;
  const [{ isSearchOpen = false }, { setIsSearchOpen } = {}] = useContext(AppContext);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [, setRecentSearches] = useLocalStorage('search:recent', '');
  const searchInputRef = useRef(null);
  const [contextData] = useContext(AppContext);
  const isNikeAiEnabled = useFeatureFlags(NIKE_AI);
  const { mounted } = useNavOptionsContext();
  const { analyticsClient } = useContext(AnalyticsContext);
  const locale = useLocale();
  const searchSuggestions = useSearchSuggestions(searchInputValue, isSearchOpen);
  const searchInputValueRef = useRef(searchInputValue);
  useSearchTermInvalidCallback(searchInputValue, () => setSearchInputValue(''));
  useSearchDocumentKeyListener({
    isSearchOpen,
    setIsSearchOpen,
    searchInputValueRef,
    setSearchInputValue,
  });

  const localizedPlaceholder = useBodega('nav_header_label_search', SEARCH);
  const localizedAriaDescription = useBodega('nav_header_label_search_input', SEARCH_PRODUCTS);
  const localizedSearchIconLabel = useBodega('nav_header_label_search', SEARCH);
  const localizedEndIconAriaLabel = useBodega('nav_header_label_resetSearch', RESET_SEARCH);
  const localizedCancelBtnText = useBodega('nav_search_cancel', CANCEL);
  const searchAriaLabel = useBodega('nav_header_label_search_extended', SEARCH_EXTENDED);

  // When the page loads with a pre-filled query param (?q= or ?vst=), the search input
  // value may be stale due to SSR hydration (useState preserves server-rendered state).
  // Re-read the query params after mount and sync both the input value and open state
  // so the end icon (clear button) is visible.
  useEffect(() => {
    const params = getLocationSearchParams();
    const initialQuery = params.get('q') || params.get('vst') || '';
    if (initialQuery) {
      setSearchInputValue(initialQuery);
    }
  }, []);

  useEffect(() => {
    if (mounted && analyticsClient && isSearchOpen) {
      analyticsClient.actionSearchUserExperienceViewed();
    }
  }, [mounted, analyticsClient, isSearchOpen]);

  useEffect(() => {
    document.body.classList[isSearchOpen ? 'add' : 'remove'](navMenuOpen);
    const searchInputElement = searchInputRef.current?.children?.namedItem(searchInputId);
    if (!isSearchOpen) {
      searchInputElement?.setCustomValidity('');
      return;
    }
    searchInputElement?.focus();
    return () => document.body.classList.remove(navMenuOpen);
  }, [isSearchOpen]);

  useEffect(() => {
    searchInputValueRef.current = searchInputValue;
  }, [searchInputValue]);

  const handleTouchAndMouseDown = () => {
    if (!isSearchOpen) {
      setIsSearchOpen(true);
      analyticsClient?.clickSearchOpen();
    }
  };

  const handleKeyDown = keyEvent => {
    if (
      !isSearchOpen &&
      (keyEvent.key === ' ' || keyEvent.key === 'Enter' || keyEvent.key === 'ArrowDown')
    ) {
      setIsSearchOpen(true);
      analyticsClient?.clickSearchOpen();
    }
    if (isSearchOpen && searchInputValue && keyEvent.key === 'Enter') {
      analyticsClient?.submitNavOnsiteSearchUserTyped({ searchItem: searchInputValue });
      setRecentSearches(searchInputValue);
      window.location.assign(
        getSearchUrl({ locale, searchTerm: searchInputValue, query: searchInputValue, path: 'w' }),
      );
    }
  };

  const handleSearchEndIconClick = () => {
    if (searchInputValue.trim().length) {
      analyticsClient?.clickSearchClear();
    }
    setSearchInputValue('');
  };

  const handleSearchIconClick = () => {
    if (!isSearchOpen) {
      setIsSearchOpen(true);
      analyticsClient?.clickSearchOpen();
    }
  };

  const handleCancelBtnClick = () => {
    if (searchInputValue.trim().length) {
      analyticsClient?.clickSearchClear();
    }
    setIsSearchOpen(false);
    setSearchInputValue('');
    analyticsClient?.clickSearchClose();
    document.getElementById(searchInputId)?.setCustomValidity('');
  };

  const pleaseEnterQueryMsg = useBodega(
    'nav_header_please_enter_search',
    PLEASE_ENTER_SEARCH_QUERY,
  );

  const handleSearchInputChange = event => {
    if (event.target?.validity?.valueMissing) {
      event.target?.setCustomValidity(pleaseEnterQueryMsg);
    } else {
      event.target?.setCustomValidity('');
    }
    if (searchInputValue.trim().length && !event?.target?.value.trim().length) {
      analyticsClient?.clickSearchClear();
    }
    if (!isSearchOpen) {
      setIsSearchOpen(true);
      analyticsClient?.clickSearchOpen();
    }
    setSearchInputValue(event.target.value);
  };

  const handleInputFocus = () => {
    if (searchInputValue) {
      setIsSearchOpen(true);
      analyticsClient?.clickSearchOpen();
    }
  };

  const searchBarClass = clsx(`gn-search-bar`, contextData.isTopBarVisible && `top-bar-visible`);

  return (
    <FocusTrap
      active={isSearchOpen && shouldTrapFocus}
      focusTrapOptions={{ allowOutsideClick: true }}
    >
      <StyledSearch
        aria-label={searchAriaLabel}
        cancelText={localizedCancelBtnText}
        data-testid="visual-search-container"
      >
        <form
          onSubmit={event => {
            event.preventDefault();
          }}
        >
          <SearchBar
            SearchResultsComponent={
              <StyledVisualSearchResultsContainer>
                <VisualSearchResults
                  isOpened={isSearchOpen}
                  searchResultsData={searchSuggestions}
                  searchTerm={searchInputValue}
                />
              </StyledVisualSearchResultsContainer>
            }
            cancelLabel={localizedCancelBtnText}
            className={searchBarClass}
            isSearchBarOpen={isSearchOpen}
            onCancelBtnClick={handleCancelBtnClick}
            searchInputProps={{
              id: searchInputId,
              value: searchInputValue,
              placeholder: localizedPlaceholder,
              required: isSearchOpen ? true : undefined,
              'aria-required': isSearchOpen ? true : undefined,
              inputAriaLabel: localizedAriaDescription,
              startIcon: isNikeAiEnabled ? <NikeAISearchIcon /> : undefined,
              startIconAriaLabel: localizedSearchIconLabel,
              startIconBtnProps: { id: SEARCH_ICON },
              endIconAriaLabel: localizedEndIconAriaLabel,
              endIconBtnProps: { id: SEARCH_END_ICON },
              onChange: handleSearchInputChange,
              onEndIconClick: handleSearchEndIconClick,
              onKeyDown: handleKeyDown,
              onStartIconClick: handleSearchIconClick,
              onMouseDown: handleTouchAndMouseDown,
              onTouchStart: handleTouchAndMouseDown,
              onFocus: handleInputFocus,
              ref: searchInputRef,
            }}
            shouldOpenOnKeyDown={false}
          />
          {children}
        </form>
      </StyledSearch>
    </FocusTrap>
  );
};

export default VisualSearch;

VisualSearch.propTypes = {
  children: node,
  shouldTrapFocus: bool,
};
